/**
 * Moments → decide (Phase C, slice 1).
 *
 * Until now the proactive decisioner ran on ticks: page view and SPA
 * navigation. The widget already detects the visitor's moments for its
 * own appearance rules (trigger-watcher.ts) but never told the agent.
 * This watches three moments worth a decision and reports each ONCE per
 * page: a reading dwell, a scroll past most of the page, and an exit
 * move. The caller posts /v1/decide with the moment NAMED so the
 * decisioner answers "is this moment worth a word" instead of "it's
 * been a while, anything to say".
 */
export type Moment = { kind: 'dwell' | 'scroll' | 'exit'; detail?: string };

export type MomentWatcherHandle = {
  /** New page (SPA nav): every moment may fire again. */
  reset: () => void;
  cleanup: () => void;
};

export function installMomentWatchers(
  onMoment: (m: Moment) => void,
  options?: { dwellMs?: number; scrollPct?: number; win?: Window; doc?: Document },
): MomentWatcherHandle {
  const win = options?.win ?? (typeof window !== 'undefined' ? window : undefined);
  const doc = options?.doc ?? (typeof document !== 'undefined' ? document : undefined);
  const dwellMs = options?.dwellMs ?? 20_000;
  const scrollPct = options?.scrollPct ?? 60;
  const fired = new Set<Moment['kind']>();
  let dwellTimer: ReturnType<typeof setTimeout> | null = null;

  const fire = (m: Moment): void => {
    if (fired.has(m.kind)) return;
    fired.add(m.kind);
    onMoment(m);
  };
  const armDwell = (): void => {
    if (dwellTimer) clearTimeout(dwellTimer);
    dwellTimer = setTimeout(() => fire({ kind: 'dwell', detail: `${Math.round(dwellMs / 1000)}s` }), dwellMs);
  };
  const onScroll = (): void => {
    if (!win || !doc) return;
    const top = win.scrollY ?? win.pageYOffset ?? 0;
    const view = win.innerHeight ?? 0;
    const total = doc.documentElement.scrollHeight ?? 1;
    if (((top + view) / total) * 100 >= scrollPct) fire({ kind: 'scroll', detail: `${scrollPct}%` });
  };
  const onMove = (e: MouseEvent): void => {
    if (e.clientY <= 20) fire({ kind: 'exit' });
  };

  armDwell();
  win?.addEventListener('scroll', onScroll, { passive: true });
  doc?.addEventListener('mousemove', onMove);

  return {
    reset() {
      fired.clear();
      armDwell();
    },
    cleanup() {
      if (dwellTimer) clearTimeout(dwellTimer);
      win?.removeEventListener('scroll', onScroll);
      doc?.removeEventListener('mousemove', onMove);
    },
  };
}
